// API and Socket configuration
// Native apps default to production. Web uses its own origin.
// Override with EXPO_PUBLIC_API_URL for local dev (e.g. http://localhost:3000).
//
// IMPORTANT: For OTA updates, NEVER run `eas update` directly — Expo bakes
// whatever EXPO_PUBLIC_API_URL is in the env / .env.local (which is
// http://localhost:3000 for local dev) into the bundle. A production bundle
// pointed at localhost can't reach the backend and every request/socket fails
// with "check your internet connection." Always publish via:
//   ./scripts/ota-update.sh --branch production --platform all --message "..."
// which forces the correct EXPO_PUBLIC_API_URL and verifies it before publish.
// The release-build safety net below is a second line of defense.
import { Platform } from 'react-native';
import { getMacSidebarWidth, isCompactMacWindow, MAC_MIN_CHAT_WIDTH } from '@/lib/macResponsiveLayout';

// The channel is embedded in the installed iOS/Android binary. Unlike an
// exported environment variable, a mistaken OTA cannot change this boundary.
const installedUpdateChannel = ['ios', 'android'].includes(Platform.OS)
  ? require('expo-updates').channel
  : null;
const isProductionChannel = ['production', 'production-playstore'].includes(installedUpdateChannel);

const API_URL_PRODUCTION = 'https://gab.ai';

const getWebOrigin = () => {
  if (typeof window === 'undefined') return null;
  const { hostname, origin } = window.location || {};
  if (!hostname || !origin) return null;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }
  return origin;
};

// Matches local-dev hosts: localhost, loopback, and the private LAN ranges
// `expo start` hands out (e.g. http://192.168.x.x:8081).
const isLocalDevHost = (h) =>
  /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0|10\.\d|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/i.test(h || '');

// SAFETY NET: a release NATIVE build must never talk to a local dev host. If a
// localhost URL leaked into a production bundle (e.g. an OTA published without
// forcing the env), discard it and use production so the app can still reach
// the backend. Dev builds (`__DEV__ === true`, Expo Go / dev client) keep
// using localhost so local development is unaffected; web uses its own origin.
const sanitizeHost = (host) => {
  if (Platform.OS !== 'web' && !__DEV__ && isProductionChannel && host !== API_URL_PRODUCTION) {
    console.warn('[config] Ignoring a non-production endpoint on the production update channel.');
    return API_URL_PRODUCTION;
  }
  if (Platform.OS !== 'web' && !__DEV__ && isLocalDevHost(host)) {
    console.warn(`[config] Ignoring local dev host "${host}" in a release build; using ${API_URL_PRODUCTION}.`);
    return API_URL_PRODUCTION;
  }
  return host;
};

export const API_HOST = sanitizeHost(
  process.env.EXPO_PUBLIC_API_URL || getWebOrigin() || API_URL_PRODUCTION,
);

// Socket host defaults to same as API host (Socket.IO handles ws/wss upgrade)
// Can be overridden separately via EXPO_PUBLIC_SOCKET_URL if needed
export const SOCKET_HOST = sanitizeHost(process.env.EXPO_PUBLIC_SOCKET_URL || API_HOST);
// Timeout for detecting stalled streams AFTER answer tokens start flowing.
// Long-context responses can have intermittent gaps between token batches,
// so keep this tolerant to avoid premature recovery on healthy streams.
export const STREAM_STALL_TIMEOUT_MS = 20000;

// Timeout while the model is still in the reasoning/thinking phase (thinking
// content may be streaming, but no answer tokens yet). Reasoning models often
// pause 30–90s between CoT chunks; the tighter STREAM_STALL_TIMEOUT_MS causes
// premature recovery that can collapse the turn into an empty assistant bubble.
export const THINKING_STALL_TIMEOUT_MS = 90000;

// Timeout for detecting stalled preprocessing (before tokens start flowing).
// Preprocessing very large chats/files can pause for longer stretches, so this
// should be significantly larger than STREAM_STALL_TIMEOUT_MS.
export const PREPROCESSING_STALL_TIMEOUT_MS = 90000;

// Maximum consecutive recovery attempts where backend reports still inferencing.
// Keep this generous so long-running generations are not cut off by the client.
export const MAX_STALL_RECOVERY_ATTEMPTS = 12;

// Only force-finish when there has been a prolonged period of complete inactivity.
// This prevents valid long responses from being truncated while still giving us an
// eventual escape hatch for truly stuck streams.
export const MAX_INFERENCE_IDLE_BEFORE_FORCE_FINISH_MS = 5 * 60 * 1000;

// How long after the last user message the addChats merge protects a locally-true
// isInferencing/isInferencingWaiting flag from being clobbered by REST payloads
// (which hard-code both to false). Covers the preprocessing window — user sent,
// no assistant message yet — which can exceed PREPROCESSING_STALL_TIMEOUT_MS for
// very large chats. Bounded so a genuinely stuck flag can still be healed by a
// later backend refresh once the window expires.
export const IN_FLIGHT_TURN_MERGE_GUARD_MS = 2 * 60 * 1000;

// How long after a send the stream-recovery path distrusts a "not inferencing"
// status response when the local conversation is still waiting on its first
// assistant reply. The status flag can lag job registration by a few seconds,
// and clearing the waiting UI in that window causes the "thinking drops to
// blank, then restarts" flicker. Kept short so recovery still injects
// completed replies promptly after backgrounding.
export const RECOVERY_STATUS_TRUST_WINDOW_MS = 15000;

export const APP_NAME = 'Gab AI';
export const APP_VERSION = '3.0';

// Build identifier — stamped into every HTML file by scripts/stamp-sw-cache.js
// after export (window.__GAB_BUILD_ID__). The diagnostic page uses this to
// detect version skew between the running bundle, the deployed origin
// (/build.json), and the service worker's remembered build.
export const BUILD_ID = (() => {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.__GAB_BUILD_ID__) {
    return window.__GAB_BUILD_ID__;
  }
  return process.env.EXPO_PUBLIC_BUILD_ID || null;
})();

// Stable schema.org @id for the one Gab AI `SoftwareApplication` node. The node
// itself is declared in
// `app/+html.jsx`, which ships on every page. Anywhere else that needs to talk
// about the Gab AI app references this @id so parsers merge the nodes instead of
// seeing competing copies of the same product. Mirrored server-side in
// `backend/utils/seoLinkBlock.js`; keep the two in sync.
export const GAB_AI_SOFTWARE_ID = 'https://gab.ai/#software';
// Which theme a device that has never picked one lands on. The signed-in app is
// Muted; the logged-out marketing pages are light. Both are only defaults — once
// the user picks a theme it is stored and wins on every surface.
export const DEFAULT_APP_THEME = 'muted';
export const DEFAULT_PUBLIC_THEME = 'white';
export const DEFAULT_ACCENT_COLOR = 'multicolor';

// Default model slugs - system-wide defaults (slugs are consistent across environments)
export const DEFAULT_IMAGE_MODEL_SLUG = 'nano-banana-2';
export const DEFAULT_VIDEO_MODEL_SLUG = 'veo-3-1-fast';
export const DEFAULT_MUSIC_MODEL_SLUG = 'music-2-6';
// Text-to-3D default. Used when an auto-send (e.g. ExploreFloatingInput's "3D"
// button) doesn't supply a modelId — without this the auto-send falls back
// to DEFAULT_IMAGE_MODEL_SLUG and a 3D request silently runs through an image
// model. Backend has its own override fallback to the same slug.
export const DEFAULT_3D_MODEL_SLUG = 'meshy-v6-text-to-3d';
// Image-to-3D default for image-to-3d auto-sends.
export const DEFAULT_IMAGE_TO_3D_MODEL_SLUG = 'meshy-v6-image-to-3d';
export const DEFAULT_ACCENT_TEXT_COLOR = '#FFFFFF';
export const DEFAULT_SHOW_BORDERS = false;
export const DEFAULT_LANGUAGE = 'en';
// Per-memory content length. Mirrors backend config.memory.contentMaxChars —
// the server clamps to the same value, so these must be changed together.
export const MEMORY_CHAR_LIMIT = 1000;
// Dream-worker / user-edited profile overview. Mirrors User.memoryDreamState.profileSummary maxlength.
export const PROFILE_SUMMARY_CHAR_LIMIT = 800;

export const DEFAULT_SIDEBAR_WIDTH = 260;
export const DEFAULT_SIDEBAR_WIDTH_MIN = 200;
export const DEFAULT_SIDEBAR_WIDTH_MAX = 260;
export const ARTIFACT_NAV_RAIL_WIDTH = 60;
// Mac collapse gives the conversation the full window; web keeps its icon rail.
export const getCollapsedNavigationSidebarWidth = () => Platform.OS === 'macos'
  ? 0
  : ARTIFACT_NAV_RAIL_WIDTH;

export const clampSidebarWidth = (width) => Math.min(
  DEFAULT_SIDEBAR_WIDTH_MAX,
  Math.max(DEFAULT_SIDEBAR_WIDTH_MIN, Number.isFinite(width) ? width : DEFAULT_SIDEBAR_WIDTH),
);

export const DESKTOP_EDITOR_SIDEBAR_TYPES = ['document-editor', 'presentation-editor', 'spreadsheet-editor'];
export const ARTIFACT_SIDEBAR_TYPES = [
  ...DESKTOP_EDITOR_SIDEBAR_TYPES,
  'pdf-preview', 'pptx-preview', 'code-preview', 'video-preview', 'video-project-preview', 'image-editor', 'project-editor',
];
export const isDesktopEditorSidebarType = (type) => DESKTOP_EDITOR_SIDEBAR_TYPES.includes(type);
export const isArtifactSidebarType = (type) => ARTIFACT_SIDEBAR_TYPES.includes(type);
// Project files sit beside normal Mac navigation; opening them must not
// replace the user's sidebar visibility preference with artifact collapse.
export const isArtifactNavigationActive = (state) => (Platform.OS === 'web'
  || (Platform.OS === 'macos' && state.contextSidebarType === 'video-project-preview'))
  && !!state.isContextSidebarVisible && isArtifactSidebarType(state.contextSidebarType);

const isNavigationSidebarCollapsed = (state, viewportWidth) => ['web', 'macos'].includes(Platform.OS) && (
  (Platform.OS === 'macos' && isCompactMacWindow(viewportWidth)) || (
    isArtifactNavigationActive(state)
      ? !state.artifactNavigationExpanded
      : state.isSidebarVisible === false
));
export const isNavigationRailActive = (state, viewportWidth) => Platform.OS === 'web'
  && isNavigationSidebarCollapsed(state, viewportWidth);

// Artifact navigation is temporary: preserve the normal menu preference while
// reserving the full menu width whenever the user expands the sidebar.
export const getNavigationSidebarWidth = (state, viewportWidth) => {
  if (isNavigationSidebarCollapsed(state, viewportWidth)) return getCollapsedNavigationSidebarWidth();
  if (!isArtifactNavigationActive(state) && state.isSidebarVisible === false) return 0;
  const width = clampSidebarWidth(state.sidebarWidth);
  return Platform.OS === 'macos' ? getMacSidebarWidth(viewportWidth) : width;
};

// Contextual (right) sidebar sizing
export const DEFAULT_CONTEXT_SIDEBAR_WIDTH = 320;
export const DEFAULT_CONTEXT_SIDEBAR_WIDTH_MIN = 220;
export const DEFAULT_CONTEXT_SIDEBAR_WIDTH_MAX = 1100;
export const PDF_PREVIEW_WIDTH_MIN = 480;
export const PDF_PREVIEW_WIDTH_MAX = 800;
export const PDF_PREVIEW_WIDTH_RATIO = 0.5;
export const PPTX_PREVIEW_WIDTH_MIN = 520;
export const PPTX_PREVIEW_WIDTH_MAX = 900;
export const PPTX_PREVIEW_WIDTH_RATIO = 0.52;
export const CODE_PREVIEW_WIDTH_MIN = 520;
export const CODE_PREVIEW_WIDTH_MAX = 860;
export const CODE_PREVIEW_WIDTH_RATIO = 0.48;
export const PRESENTATION_EDITOR_WIDTH_MIN = 580;
export const PRESENTATION_EDITOR_WIDTH_MAX = 900;
export const PRESENTATION_EDITOR_WIDTH_RATIO = 0.58;
export const DOCUMENT_EDITOR_WIDTH_MIN = 640;
export const DOCUMENT_EDITOR_WIDTH_MAX = 860;
export const DOCUMENT_EDITOR_WIDTH_RATIO = 0.52;
export const SPREADSHEET_EDITOR_WIDTH_MIN = 620;
export const SPREADSHEET_EDITOR_WIDTH_MAX = 900;
export const SPREADSHEET_EDITOR_WIDTH_RATIO = 0.52;
export const VIDEO_PROJECT_PREVIEW_WIDTH_MIN = 560;
export const VIDEO_PROJECT_PREVIEW_WIDTH_MAX = 920;
export const VIDEO_PROJECT_PREVIEW_WIDTH_RATIO = 0.42;
export const CHAT_DETAILS_WIDTH_MIN = 260;
export const CHAT_DETAILS_WIDTH_MAX = 460;
export const CHAT_DETAILS_WIDTH_RATIO = 0.22;

// [min, max, ratio-of-viewport] per contextual sidebar type. Each type keeps
// its own remembered width (see contextSidebarWidths): the editors want roughly
// half the window, while Details is a narrow reading panel, and a single shared
// width meant whichever opened last resized the other.
export const CONTEXT_SIDEBAR_SIZING = {
  'project-editor': [560, 1200, 0.6],
  'chat-details': [CHAT_DETAILS_WIDTH_MIN, CHAT_DETAILS_WIDTH_MAX, CHAT_DETAILS_WIDTH_RATIO],
  'pdf-preview': [PDF_PREVIEW_WIDTH_MIN, PDF_PREVIEW_WIDTH_MAX, PDF_PREVIEW_WIDTH_RATIO],
  'pptx-preview': [PPTX_PREVIEW_WIDTH_MIN, PPTX_PREVIEW_WIDTH_MAX, PPTX_PREVIEW_WIDTH_RATIO],
  'image-editor': [PRESENTATION_EDITOR_WIDTH_MIN, PRESENTATION_EDITOR_WIDTH_MAX, PRESENTATION_EDITOR_WIDTH_RATIO],
  'code-preview': [CODE_PREVIEW_WIDTH_MIN, CODE_PREVIEW_WIDTH_MAX, CODE_PREVIEW_WIDTH_RATIO],
  'document-editor': [DOCUMENT_EDITOR_WIDTH_MIN, DOCUMENT_EDITOR_WIDTH_MAX, DOCUMENT_EDITOR_WIDTH_RATIO],
  'presentation-editor': [PRESENTATION_EDITOR_WIDTH_MIN, PRESENTATION_EDITOR_WIDTH_MAX, PRESENTATION_EDITOR_WIDTH_RATIO],
  'spreadsheet-editor': [SPREADSHEET_EDITOR_WIDTH_MIN, SPREADSHEET_EDITOR_WIDTH_MAX, SPREADSHEET_EDITOR_WIDTH_RATIO],
  'video-preview': [420, 900, 0.5],
  'video-project-preview': [VIDEO_PROJECT_PREVIEW_WIDTH_MIN, VIDEO_PROJECT_PREVIEW_WIDTH_MAX, VIDEO_PROJECT_PREVIEW_WIDTH_RATIO],
};

export const getContextSidebarWidthBounds = (type) => {
  const sizing = CONTEXT_SIDEBAR_SIZING[type];
  return sizing
    ? [sizing[0], sizing[1]]
    : [DEFAULT_CONTEXT_SIDEBAR_WIDTH_MIN, DEFAULT_CONTEXT_SIDEBAR_WIDTH_MAX];
};

// Chat column that must remain after the nav + artifact split. Below this the
// artifact sidebar covers the thread (ContextualSidebar also unmounts at XS).
// Keep CONTEXT_SIDEBAR_COMPACT_MAX in sync with XS_BREAKPOINT.
export const MIN_CHAT_BESIDE_CONTEXT_SIDEBAR = 360;
export const CONTEXT_SIDEBAR_COMPACT_MAX = 767;
export const ARTIFACT_LAYOUT_VERSION = 3;
export const ARTIFACT_SIDEBAR_WIDTH_RATIO = 0.65;

export const canFitContextSidebar = (type, windowWidth, navWidth = DEFAULT_SIDEBAR_WIDTH) => {
  if (!Number.isFinite(windowWidth) || windowWidth <= CONTEXT_SIDEBAR_COMPACT_MAX) {
    return false;
  }
  const sizing = CONTEXT_SIDEBAR_SIZING[type];
  if (!sizing || type === 'chat-details') return true;
  const nav = Math.max(0, Number.isFinite(navWidth) ? navWidth : DEFAULT_SIDEBAR_WIDTH);
  return windowWidth >= sizing[0] + nav + MIN_CHAT_BESIDE_CONTEXT_SIDEBAR;
};

// Opening the full navigation should resize an artifact, not dismiss it.
// Close only when even collapsed navigation cannot leave a usable desktop split.
export const canFitActiveContextSidebar = (state, viewportWidth) => canFitContextSidebar(
  state.contextSidebarType,
  viewportWidth,
  isArtifactNavigationActive(state) ? getCollapsedNavigationSidebarWidth() : getNavigationSidebarWidth(state, viewportWidth),
);

export const clampContextSidebarWidth = (type, width) => {
  const [min, max] = getContextSidebarWidthBounds(type);
  const requested = Number.isFinite(width) ? width : min;
  // Artifact panels can use large screens; their maximum is the available
  // viewport, rather than the former fixed 800–920px editor caps.
  return Math.min(isArtifactSidebarType(type) ? Infinity : max, Math.max(min, requested));
};

export const resolveArtifactSidebarWidth = (type, viewportWidth, navWidth = getCollapsedNavigationSidebarWidth(), preferredWidth) => {
  const [min] = getContextSidebarWidthBounds(type);
  if (!Number.isFinite(viewportWidth) || viewportWidth <= 0) return min;
  const available = Math.max(0, viewportWidth - Math.max(0, navWidth));
  const maximum = Math.max(0, available - MIN_CHAT_BESIDE_CONTEXT_SIDEBAR);
  const requested = Number.isFinite(preferredWidth) ? preferredWidth : Math.round(available * (type === 'video-preview' ? 0.5 : ARTIFACT_SIDEBAR_WIDTH_RATIO));
  return Math.min(maximum, Math.max(min, requested));
};

export const getEffectiveContextSidebarWidth = (state, viewportWidth) => {
  // Keep the panel's saved width while a small Mac window uses an overlay.
  // Details needs the same chat-space budget as the larger artifact previews.
  if (Platform.OS === 'macos' && Number.isFinite(viewportWidth)) {
    const available = viewportWidth - getNavigationSidebarWidth(state, viewportWidth);
    const [minimum] = getContextSidebarWidthBounds(state.contextSidebarType);
    if (available < minimum + MAC_MIN_CHAT_WIDTH) return 0;
    if (!isArtifactSidebarType(state.contextSidebarType)) {
      const preferred = Number.isFinite(state.contextSidebarWidth) ? state.contextSidebarWidth : DEFAULT_CONTEXT_SIDEBAR_WIDTH;
      return Math.min(preferred, available - MAC_MIN_CHAT_WIDTH);
    }
  }
  if (!isArtifactSidebarType(state.contextSidebarType)) {
    return Number.isFinite(state.contextSidebarWidth) ? state.contextSidebarWidth : DEFAULT_CONTEXT_SIDEBAR_WIDTH;
  }
  const automatic = state.contextSidebarLayoutVersion !== ARTIFACT_LAYOUT_VERSION || state.contextSidebarWidthMode === 'auto';
  const resolved = resolveArtifactSidebarWidth(
    state.contextSidebarType,
    viewportWidth,
    getNavigationSidebarWidth(state, viewportWidth),
    automatic ? undefined : state.contextSidebarWidth,
  );
  return Platform.OS === 'macos' && Number.isFinite(viewportWidth)
    ? Math.min(resolved, viewportWidth - getNavigationSidebarWidth(state, viewportWidth) - MAC_MIN_CHAT_WIDTH)
    : resolved;
};

// Width a sidebar type opens at the first time, before the user resizes it.
export const resolveContextSidebarWidth = (type, viewportWidth) => {
  if (isArtifactSidebarType(type)) return resolveArtifactSidebarWidth(type, viewportWidth);
  const sizing = CONTEXT_SIDEBAR_SIZING[type];
  if (!sizing) return DEFAULT_CONTEXT_SIDEBAR_WIDTH;
  const [min, , ratio] = sizing;
  if (!Number.isFinite(viewportWidth) || viewportWidth <= 0) return min;
  return clampContextSidebarWidth(type, Math.round(viewportWidth * ratio));
};

// Detailed chat input panel sizing (left-side vertical panel)
export const DEFAULT_CHAT_INPUT_DETAILED_WIDTH = 420;
export const DEFAULT_CHAT_INPUT_DETAILED_WIDTH_MIN = 320;
export const DEFAULT_CHAT_INPUT_DETAILED_WIDTH_MAX = 640;

// Deck (multi-column workspace) sizing & limits.
// Mirrors backend/utils/deck_limits.js — keep the two in sync.
export const DEFAULT_DECK_COLUMN_WIDTH = 420;
export const MIN_DECK_COLUMN_WIDTH = 340;
export const MAX_DECK_COLUMN_WIDTH = 800;
export const MAX_DECK_COLUMNS = 12;
export const MAX_DECKS_PER_USER = 12;

export const DEFAULT_FONT_FAMILY = 'ui-sans-serif,-apple-system, system-ui, -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, "Open Sans", Arial, "Helvetica Neue", Segoe UI Emoji, Segoe UI Symbol, sans-serif';

export const MESSAGE_LAYOUT = {
  BUBBLE: 'bubble',
  SLIM: 'slim',
  WIDE: 'wide',
};

export const BORDER_RADIUS = {
  small: 6,
  medium: 12,
  large: 16,
  round: 9999,
};

export const MESSAGE_WIDTH = {
  NARROW: 'narrow',
  MEDIUM: 'medium',
  FULL: 'full',
};

export const MESSAGE_WIDTH_AMOUNTS = {
  NARROW: 800,
  MEDIUM: 960,
  FULL: '100%',
};

export const CHAT_INPUT_STYLE = {
  FLOATING: 'floating',
  FIXED: 'fixed',
  DETAILED: 'detailed',
};

export const MESSAGE_REGENERATE_TYPES = {
  AGAIN: 'again',
  CONCISE: 'concise',
  ADD_DETAILS: 'add_details',
};

// : todo :
// import { getLocales } from 'expo-localization';
// const deviceLanguage = getLocales()[0].languageCode;

export const GHOST_TRIAL_DAYS = 1;
export const AGENT_ICON = 'train';
export const MAX_MODELS_SELECTABLE = 5;

export const AGENT_URL_PREFIX = 'agent://';

export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB — must match backend/config.js maxFileSize
export const MAX_FILES_UPLOADED_IN_MESSAGE = 10;
export const FILE_SIZE_5MB = 5 * 1024 * 1024; // 5MB

export const ALLOWED_INPAINTING_FILE_TYPES = ['image'];

// Free user starting credits
export const FREE_USER_STARTING_CREDITS = 150;
export const FREE_USER_UNVERIFIED_CREDITS = 30;
export const FREE_USER_VERIFICATION_BONUS_CREDITS = FREE_USER_STARTING_CREDITS - FREE_USER_UNVERIFIED_CREDITS;

// Plus monthly credit allotment
export const PLUS_MONTHLY_CREDITS = 2000;
// Plus Premium (web-only) monthly credit allotment
export const PLUS_PREMIUM_MONTHLY_CREDITS = 12000;

// Daily API request limits, mirroring backend config.limits
export const API_DAILY_LIMIT_PLUS = 10000;
export const API_DAILY_LIMIT_PREMIUM = 50000;
export const API_DAILY_LIMIT_PLUS_DISPLAY = API_DAILY_LIMIT_PLUS.toLocaleString();
export const API_DAILY_LIMIT_PREMIUM_DISPLAY = API_DAILY_LIMIT_PREMIUM.toLocaleString();

// Formatted versions for display in UI strings
export const FREE_USER_STARTING_CREDITS_DISPLAY = FREE_USER_STARTING_CREDITS.toLocaleString();
export const PLUS_MONTHLY_CREDITS_DISPLAY = PLUS_MONTHLY_CREDITS.toLocaleString();
export const PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY = PLUS_PREMIUM_MONTHLY_CREDITS.toLocaleString();

// Default model is free for Plus in the app; API requests always use credits
export const DEFAULT_MODEL_FREE_API_NOTE = 'Not included in API';
// Lowercasing the note would render the acronym as "api", so the parenthetical
// is written out rather than derived.
export const DEFAULT_MODEL_FREE_FEATURE = 'Unlimited messages with the default AI model (in-app only)';

// Pricing plans configuration
export const PLUS_FEATURES = [
  { icon: 'briefcase', boldText: 'Work mode', text: '- up to 3 sessions at once to research, use tools, and deliver finished work', color: '#1F6B47' },
  { icon: 'infinity', boldText: 'Free default model messages', text: `- no more 1 credit each! ${DEFAULT_MODEL_FREE_API_NOTE}.`, color: '#5FB68F' },
  { icon: 'coins', boldText: `${PLUS_MONTHLY_CREDITS_DISPLAY} credits/month`, text: 'for 100+ AI models, images & more', color: '#F59E0B' },
  { icon: 'microphone', boldText: 'Voice Mode', text: '- realtime conversations with AI', color: '#10B981' },
  { icon: 'video', boldText: 'Premium creation & memory', text: '- video models, custom agents, and personalization', color: '#EC4899' },
];

export const PRICING_PLANS = {
  free: {
    name: 'Free',
    trialDays: 0, // No longer time-based
    startingCredits: FREE_USER_STARTING_CREDITS,
    description: `Start with ${FREE_USER_STARTING_CREDITS_DISPLAY} free credits to explore Gab AI.`,
    price: '$0',
    features: [
      { name: `${FREE_USER_STARTING_CREDITS_DISPLAY} free credits to start`, included: true },
      { name: 'Access to default AI model (1 credit/message)', included: true },
      { name: 'Access to all AI models (uses credits)', included: true },
      { name: 'Image, video & music generation', included: true },
      { name: 'Web search & file analysis', included: true },
      { name: '1 custom agent', included: true },
      { name: 'Work mode (Plus only)', included: false },
      { name: 'Voice Mode (Plus only)', included: false },
      { name: 'Free default model messages (Plus only)', included: false },
      { name: `${PLUS_MONTHLY_CREDITS_DISPLAY} monthly credits (Plus only)`, included: false },
    ],
  },
  plus: {
    name: 'Plus',
    description: 'Unlock Work mode for agentic, multi-step tasks—plus premium models, tools, and monthly credits.',
    features: [
      { name: 'Work mode — agents that plan, research, use tools, and deliver finished work', included: true },
      { name: 'Up to 3 Work sessions at once', included: true },
      { name: DEFAULT_MODEL_FREE_FEATURE, included: true },
      { name: `${PLUS_MONTHLY_CREDITS_DISPLAY} credits for use with other models & tools`, included: true },
      { name: 'Voice Mode — realtime AI conversations', included: true },
      { name: 'Access to all advanced AI models', included: true },
      { name: 'Unlimited custom agents', included: true },
      { name: 'Fastest response time', included: true },
      { name: 'Unlimited chat history', included: true },
      { name: 'API access (uses credits)', included: true },
    ],
    weeklyPrice: '6.99',
    monthlyPrice: '20.00',
    annualPrice: '200.00',
    annualMonthlyPrice: '16.67',
    annualWeeklyPrice: '3.85',
    bitcoinAnnualPrice: '180',
  },
  plusPremium: {
    name: 'Plus Premium',
    description: 'For businesses and power users who need more credits and higher API limits.',
    badge: 'Businesses & power users',
    features: [
      { name: 'Everything in Plus', included: true },
      { name: 'Up to 10 Work sessions at once', included: true },
      { name: `${PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY} credits every month`, included: true },
      { name: 'Higher API rate limits (50,000 requests/day)', included: true },
      { name: 'Priority email support', included: true },
    ],
    monthlyPrice: '99.00',
    annualPrice: '999.00',
    annualMonthlyPrice: '83.25',
    // ~10% off card annual, matching Plus Bitcoin ($180 vs $200)
    bitcoinAnnualPrice: '899',
  },
  enterprise: {
    name: 'Enterprise',
    description: 'Custom solutions for organizations with advanced needs and scale.',
    price: 'Custom',
    features: [
      { name: 'Everything in Plus Premium', included: true },
      { name: 'Custom AI model training', included: true },
      { name: 'Advanced analytics', included: true },
      { name: 'Custom integrations', included: true },
      { name: 'Volume discounts', included: true },
      { name: 'Custom security requirements', included: true },
    ],
  },
};

// UI Store defaults
export const DEFAULT_UI_STATE = {
  collapsedSections: { sidebarMore: true, sidebarTools: true },
  messageLayout: MESSAGE_LAYOUT.WIDE,
  messageWidth: MESSAGE_WIDTH.NARROW,
  tagsLayout: 'list',
  sidebarWidth: DEFAULT_SIDEBAR_WIDTH,
  contextSidebarWidth: DEFAULT_CONTEXT_SIDEBAR_WIDTH,
  contextSidebarWidthMode: 'auto',
  contextSidebarLayoutVersion: ARTIFACT_LAYOUT_VERSION,
  // Per-type widths, keyed by contextSidebarType.
  contextSidebarWidths: {},
  chatInputDetailedWidth: DEFAULT_CHAT_INPUT_DETAILED_WIDTH,
  increaseBorderContrast: true,
  showTagCounts: true,
  chatInputStyle: CHAT_INPUT_STYLE.FLOATING,
  showTimestamps: true,
  showMessageActions: true,
  showSourcesOutputsPanel: true,
  language: DEFAULT_LANGUAGE,
  isSidebarVisible: true,
  artifactNavigationExpanded: false,
  // Contextual sidebar (right side)
  isContextSidebarVisible: false,
  contextSidebarType: null,
  contextSidebarProps: null,
  videoProjectContext: null,
  videoTakePicks: {},
  // UI behavior: when true, pressing Enter on web sends; when false, Enter inserts newline
  enterToSend: true,
  // A new opt-in key leaves old installs with the app-wide default off, too.
  keepScreenAwakeInChats: false,
  workNotificationSounds: true,
  workNotifications: true,
  webSearchEnabled: false,
  // Last choice for "use memories in this temporary chat". null = never
  // chosen (prompt once). false = unpersonalized. true = personalized.
  temporaryChatUsePersonalization: null,
  // Public layout hero gradient opacity (web scroll effect)
  heroGradientOpacity: 1,
  // Track prompt count for free trial toast (show every 10 prompts)
  freeTrialPromptCount: 0,
};

export const MAX_FREEMIUM_CONVERSATIONS = 30;

export const DEFAULT_AI_SUGGESTIONS = [
  // { icon: 'paintbrush', color: '#34d399', key: 'create_image' },
  { icon: 'file-lines', color: '#f97316', key: 'summarize_text' },
  { icon: 'pencil', color: '#a78bfa', key: 'help_write' },
  { icon: 'chart-column', color: '#60a5fa', key: 'analyze_data' },
  { icon: 'clipboard', color: '#fbbf24', key: 'make_plan' },
  // { icon: 'eye', color: '#818cf8', key: 'analyze_images' },
  { icon: 'lightbulb', color: '#fcd34d', key: 'brainstorm' },
  { icon: 'message', color: '#4ade80', key: 'get_advice' },
  { icon: 'file-code', color: '#f97316', key: 'write_code' },
];

export const SUGGESTION_CATEGORIES = [
  { 
    id: 'write',
    icon: 'pencil',
    label: 'Write',
    prompts: [
      'Help me identify my writing weaknesses',
      'Create technical explanations',
      'Develop instructional content',
      'Write case studies',
      'Write speech drafts',
      'Draft blog posts'
    ]
  },
  {
    id: 'learn',
    icon: 'graduation-cap',
    label: 'Learn',
    prompts: [
      'Explain this concept to me',
      'Create a study plan',
      'Quiz me on this topic',
      'Summarize this information',
      'Help me understand this better',
      'Break this down step by step'
    ]
  },
  {
    id: 'code',
    icon: 'code',
    label: 'Code',
    prompts: [
      'Help me debug this code',
      'Explain this code to me',
      'Optimize this function',
      'Generate unit tests',
      'Review my code',
      'Convert this to another language'
    ]
  },
  {
    id: 'life',
    icon: 'coffee',
    label: 'Life stuff',
    prompts: [
      'Plan my weekly meals',
      'Create a workout routine',
      'Help me make a decision',
      'Give me productivity tips',
      'Create a learning plan',
      'Help me organize my tasks'
    ]
  },
  {
    id: 'claude',
    icon: 'sparkles',
    label: "Claude's choice",
    prompts: [
      'What can you help me with?',
      'Tell me about your capabilities',
      'How should I best work with you?',
      'What are your limitations?',
      'Show me some example tasks',
      'Give me tips for better prompts'
    ]
  }
];

// Image generation styles for the Images page
export const IMAGE_STYLES = [
  {
    id: 'cyberpunk',
    name: 'Cyberpunk',
    previewImagePrompt: 'A rain-soaked Tokyo alleyway at night, dense with holographic advertisements and neon signs in hot pink and electric blue, steam rising from vents, puddles reflecting the glow, a lone figure in a hooded jacket walking away, cinematic atmosphere, square format',
    previewImageId: '1112aeb1-de8a-4993-9cca-4fb2d81f66de',
    promptTemplate: 'Create a cyberpunk style image with neon lights, rain-slicked streets, and futuristic technology. High contrast, vibrant pink and blue neon colors, dystopian atmosphere.',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/1112aeb1-de8a-4993-9cca-4fb2d81f66de-sm.png',
  },
  {
    id: 'watercolor',
    name: 'Watercolor',
    previewImagePrompt: 'A loose watercolor painting of cherry blossoms on a branch, soft pink petals bleeding into wet paper, visible brushstrokes and water blooms, delicate splatter effects, white paper showing through translucent washes, artistic and dreamy, square format',
    previewImageId: '7e24ced8-01d1-43b1-8c76-d741de53df1e',
    promptTemplate: 'Create a beautiful watercolor painting with soft, flowing colors and delicate brush strokes. Gentle color blending, artistic and dreamy atmosphere.',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/7e24ced8-01d1-43b1-8c76-d741de53df1e-sm.png',
  },
  {
    id: 'anime',
    name: 'Anime',
    previewImagePrompt: 'An anime-style portrait of a young warrior with flowing silver hair and determined eyes, cherry blossom petals floating in wind, dramatic backlighting with golden hour glow, vibrant cel-shaded colors, Studio Ghibli meets modern anime aesthetic, square format',
    previewImageId: 'e17875a2-a47a-4080-8d59-884eaf1cfbec',
    promptTemplate: 'Create an anime style illustration with vibrant colors, expressive characters, and dynamic composition. Japanese animation aesthetic, detailed and colorful.',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/e17875a2-a47a-4080-8d59-884eaf1cfbec-sm.png',
  },
  {
    id: 'oil-painting',
    name: 'Oil Painting',
    previewImagePrompt: 'A classical oil painting of a vase of sunflowers on a wooden table, Rembrandt-style chiaroscuro lighting, visible impasto brushstrokes with rich texture, deep amber and gold tones against dark background, museum-quality still life in the Dutch Golden Age tradition, square format',
    previewImageId: '3735374b-f3b2-450d-8e2f-0849d16dbd14',
    promptTemplate: 'Create a classical oil painting with rich textures, dramatic lighting, and masterful brush strokes. Renaissance or Baroque style, museum quality artwork.',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/3735374b-f3b2-450d-8e2f-0849d16dbd14-sm.png',
  },
  {
    id: 'minimalist',
    name: 'Minimalist',
    previewImagePrompt: 'A single geometric shape - a perfect terracotta circle floating on a cream white background, subtle shadow beneath, vast negative space, Bauhaus-inspired composition, muted earth tone palette, elegant simplicity, modern gallery aesthetic, square format',
    previewImageId: 'ade4ef64-493a-4fe3-8cf4-ced660d53b12',
    promptTemplate: 'Create a minimalist design with clean lines, simple shapes, and limited color palette. Modern aesthetic, elegant simplicity, lots of negative space.',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/ade4ef64-493a-4fe3-8cf4-ced660d53b12-sm.png',
  },
  {
    id: 'fantasy',
    name: 'Fantasy Art',
    previewImagePrompt: 'An ancient stone tower rising from a floating island in the clouds, glowing magical runes carved into the rock, a phoenix circling overhead trailing embers, mystical purple and gold sunset sky, lush vegetation with bioluminescent flowers, epic fantasy book cover illustration style, square format',
    previewImageId: '9be3442f-9ad3-4862-bd1e-3316a81ced92',
    promptTemplate: 'Create epic fantasy artwork with magical elements, mythical creatures, and enchanted landscapes. Detailed illustration, dramatic lighting, otherworldly atmosphere.',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/9be3442f-9ad3-4862-bd1e-3316a81ced92-sm.png',
  },
  {
    id: 'vintage',
    name: 'Vintage Film',
    previewImagePrompt: 'A 1970s photograph of a woman in a sundress walking through a field of wildflowers, warm orange and brown color cast, heavy film grain, slight overexposure with light leaks on edges, faded Kodachrome colors, nostalgic summer afternoon feeling, analog Polaroid aesthetic, square format',
    previewImageId: 'fc2b0efe-9e3a-4482-bb99-ee0105b4ed3c',
    promptTemplate: 'Create a vintage film photography style image with warm tones, film grain, and nostalgic atmosphere. 1970s aesthetic, faded colors, analog camera feel.',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/61ad40c4-ae25-4f7f-b0e8-1be38fce30d4-sm.png',
  },
  {
    id: 'pop-art',
    name: 'Pop Art',
    previewImagePrompt: 'A Roy Lichtenstein style pop art portrait of a woman with bold black outlines, Ben-Day dots pattern on skin and hair, primary colors of red yellow and blue, comic book speech bubble, dramatic expression, high contrast graphic design, Andy Warhol influence, square format',
    previewImageId: '8924c2c6-6561-4fe8-a9be-404479711434',
    promptTemplate: 'Create bold pop art in the style of Andy Warhol or Roy Lichtenstein. Bright primary colors, Ben-Day dots, comic book aesthetic, high contrast.',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/8924c2c6-6561-4fe8-a9be-404479711434-sm.png',
  },
  {
    id: 'photorealistic',
    name: 'Photorealistic',
    previewImagePrompt: 'A photorealistic close-up of a dewdrop on a leaf at golden hour, perfect reflection of surrounding forest visible in the droplet, razor sharp focus, professional macro photography, natural soft bokeh background, ultra high definition detail in every vein of the leaf, square format',
    previewImageId: 'e8a545e2-c90f-4852-9b05-ad5276b6cad3',
    promptTemplate: 'Create a photorealistic image with incredible detail and lifelike quality. Professional photography level, perfect lighting, sharp focus, ultra high definition.',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/e8a545e2-c90f-4852-9b05-ad5276b6cad3-sm.png',
  },
  {
    id: 'neon-glow',
    name: 'Neon Glow',
    previewImagePrompt: 'A glowing neon flamingo sign against a pitch black background, hot pink light radiating outward with soft bloom effect, electric blue accent lights, visible glass tubing, retro bar sign aesthetic, synthwave color palette, light reflecting off nearby surfaces, square format',
    previewImageId: '0bf2e1c8-668b-4336-a943-584e55aa8dbd',
    promptTemplate: 'Create an image with vibrant neon glow effects, dark background, and luminous colors. Electric atmosphere, glowing outlines, synthwave aesthetic.',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/0bf2e1c8-668b-4336-a943-584e55aa8dbd-sm.png',
  },
];

// Creative prompt suggestions for the Images page discover section
export const IMAGE_PROMPT_SUGGESTIONS = [
  {
    id: 'dragon-treasure',
    text: 'A majestic dragon guarding a mountain of treasure',
    thumbnail: 'https://images.unsplash.com/photo-1577493340887-b7bfff550145?w=200&h=200&fit=crop',
    previewImagePrompt: 'A massive crimson dragon with golden eyes coiled protectively around a glittering mountain of gold coins and jewels, ancient stone cave interior, shafts of light illuminating the hoard, smoke wisping from nostrils, detailed scales, epic fantasy illustration, square format',
    previewImageId: '68847ba3-a2bc-4c49-b192-1db5f430f379',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/68847ba3-a2bc-4c49-b192-1db5f430f379-sm.png',
  },
  {
    id: 'space-station',
    text: 'Futuristic space station orbiting a distant planet',
    thumbnail: 'https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=200&h=200&fit=crop',
    previewImagePrompt: 'A massive ring-shaped space station orbiting a gas giant with swirling purple and orange clouds, solar panels gleaming in starlight, smaller spacecraft docking, distant sun creating rim lighting, hard science fiction aesthetic, cinematic space art, square format',
    previewImageId: 'ea022ee5-34d9-49b3-beba-fc64782017e1',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/ea022ee5-34d9-49b3-beba-fc64782017e1-sm.png',
  },
  {
    id: 'enchanted-forest',
    text: 'An enchanted forest with glowing mushrooms and fireflies',
    thumbnail: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=200&h=200&fit=crop',
    previewImagePrompt: 'A mystical forest path at twilight with oversized bioluminescent mushrooms in blue and violet hues, hundreds of golden fireflies floating in misty air, ancient moss-covered trees with twisted roots, magical fairy tale atmosphere, soft ethereal lighting, square format',
    previewImageId: '0e666370-aa2a-40c8-bd3a-1f97b9a68444',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/0e666370-aa2a-40c8-bd3a-1f97b9a68444-sm.png',
  },
  {
    id: 'underwater-city',
    text: 'A mystical underwater city with bioluminescent architecture',
    thumbnail: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=200&h=200&fit=crop',
    previewImagePrompt: 'An ancient underwater city with domed buildings made of luminescent coral and pearl, schools of colorful fish swimming between spires, bioluminescent jellyfish serving as floating lanterns, shafts of sunlight filtering from surface above, Atlantis fantasy aesthetic, square format',
    previewImageId: '60cc250f-50e0-4ff8-8d77-00edcd56be2f',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/60cc250f-50e0-4ff8-8d77-00edcd56be2f-sm.png',
  },
  {
    id: 'steampunk-airship',
    text: 'Victorian steampunk airship flying through clouds',
    thumbnail: 'https://images.unsplash.com/photo-1534088568595-a066f410bcda?w=200&h=200&fit=crop',
    previewImagePrompt: 'An ornate Victorian steampunk airship with brass propellers and copper pipes, canvas balloons and wooden hull, steam billowing from exhaust vents, flying through golden sunset clouds, gears and clockwork visible, Jules Verne inspired adventure aesthetic, square format',
    previewImageId: 'db4f38df-31b8-403c-a20d-b10a62e2f7cf',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/db4f38df-31b8-403c-a20d-b10a62e2f7cf-sm.png',
  },
  {
    id: 'aurora-landscape',
    text: 'Northern lights dancing over a snowy mountain landscape',
    thumbnail: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=200&h=200&fit=crop',
    previewImagePrompt: 'Vibrant green and purple aurora borealis ribbons dancing across a star-filled Arctic sky, snow-capped mountain peaks below, frozen lake reflecting the lights, lone pine trees silhouetted, crisp winter night in Norway or Iceland, long exposure photography style, square format',
    previewImageId: '97d8f141-ad20-48ac-b304-285e5f69f9bf',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/97d8f141-ad20-48ac-b304-285e5f69f9bf-sm.png',
  },
];

// Video generation styles for the Videos page
export const VIDEO_STYLES = [
  {
    id: 'cinematic',
    name: 'Cinematic',
    previewImagePrompt: 'A director silhouetted behind a professional film camera on set, golden hour light streaming through dusty air, dramatic lens flare, shallow depth of field with blurred crew in background, moody teal and orange color grading, anamorphic widescreen feel, square format',
    previewImageId: '2a18d0a7-aeec-478c-b07b-e4cd6adf0812',
    promptTemplate: 'Create a cinematic video with dramatic lighting, wide shots, and film-like color grading. Professional cinematography, shallow depth of field, smooth camera movements.',
    previewVideoId: 'ecea7657-22b7-468f-9da8-1d36a5b78a56',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/2a18d0a7-aeec-478c-b07b-e4cd6adf0812-sm.png',
    previewVideoCdn: 'https://cdn.gab.ai/assets/preview/videos/ecea7657-22b7-468f-9da8-1d36a5b78a56.mp4',
    previewGifCdn: 'https://cdn.gab.ai/assets/preview/gifs/ecea7657-22b7-468f-9da8-1d36a5b78a56.gif',
  },
  {
    id: 'animation',
    name: 'Animation',
    previewImagePrompt: 'A colorful animation frame showing a cartoon fox mid-leap through a stylized forest, bold outlines and flat color fills, dynamic motion lines, squash and stretch exaggeration, Pixar meets hand-drawn aesthetic, vibrant palette of oranges and greens, square format',
    previewImageId: 'd1f9acf1-1bab-4633-9f5b-036c689c7c9c',
    promptTemplate: 'Create an animated video with fluid motion, vibrant colors, and expressive characters. Stylized animation, smooth transitions, dynamic action.',
    previewVideoId: '1147859d-662b-42e6-b8e7-799314474887',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/d1f9acf1-1bab-4633-9f5b-036c689c7c9c-sm.png',
    previewVideoCdn: 'https://cdn.gab.ai/assets/preview/videos/1147859d-662b-42e6-b8e7-799314474887.mp4',
    previewGifCdn: 'https://cdn.gab.ai/assets/preview/gifs/1147859d-662b-42e6-b8e7-799314474887.gif',
  },
  {
    id: 'slow-motion',
    name: 'Slow Motion',
    previewImagePrompt: 'A frozen moment of a water balloon bursting in extreme slow motion, suspended water droplets catching rainbow light, high-speed photography aesthetic, black background, crystalline detail of liquid in mid-explosion, dramatic studio lighting, square format',
    previewImageId: '11250a39-2f53-4b1c-bb31-ea14fbaa7e49',
    promptTemplate: 'Create a slow motion video that captures every detail of movement. High frame rate, smooth playback, dramatic reveal of motion, artistic and mesmerizing.',
    previewVideoId: 'e4932348-4bb7-44c1-87d9-0bde069554c0',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/11250a39-2f53-4b1c-bb31-ea14fbaa7e49-sm.png',
    previewVideoCdn: 'https://cdn.gab.ai/assets/preview/videos/e4932348-4bb7-44c1-87d9-0bde069554c0.mp4',
    previewGifCdn: 'https://cdn.gab.ai/assets/preview/gifs/e4932348-4bb7-44c1-87d9-0bde069554c0.gif',
  },
  {
    id: 'timelapse',
    name: 'Timelapse',
    previewImagePrompt: 'A composite timelapse image showing star trails circling around Polaris above a desert landscape, curved light streaks across night sky, silhouetted Joshua trees in foreground, deep blue to purple gradient sky, long exposure photography capturing hours of movement, square format',
    previewImageId: '473c2786-1763-447a-8c83-72bb3873a7f0',
    promptTemplate: 'Create a timelapse video showing the passage of time. Compressed time, smooth transitions between frames, dramatic transformation, hypnotic motion.',
    previewVideoId: '55df7bee-4e01-41be-91e4-432d17330cc7',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/473c2786-1763-447a-8c83-72bb3873a7f0-sm.png',
    previewVideoCdn: 'https://cdn.gab.ai/assets/preview/videos/55df7bee-4e01-41be-91e4-432d17330cc7.mp4',
    previewGifCdn: 'https://cdn.gab.ai/assets/preview/gifs/55df7bee-4e01-41be-91e4-432d17330cc7.gif',
  },
  {
    id: 'documentary',
    name: 'Documentary',
    previewImagePrompt: 'A candid documentary photograph of weathered hands of an elderly craftsman shaping clay on a pottery wheel, natural window light, shallow depth of field, authentic workshop environment with tools in background, National Geographic style storytelling, raw and intimate, square format',
    previewImageId: '6bb964d7-a0c7-445d-8eab-a00c59efa360',
    promptTemplate: 'Create a documentary style video with realistic footage, natural lighting, and authentic atmosphere. Observational camera work, informative visuals.',
    previewVideoId: '90dcf59b-1a1e-4a98-b291-55bc3db1ca33',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/6bb964d7-a0c7-445d-8eab-a00c59efa360-sm.png',
    previewVideoCdn: 'https://cdn.gab.ai/assets/preview/videos/90dcf59b-1a1e-4a98-b291-55bc3db1ca33.mp4',
    previewGifCdn: 'https://cdn.gab.ai/assets/preview/gifs/90dcf59b-1a1e-4a98-b291-55bc3db1ca33.gif',
  },
  {
    id: 'music-video',
    name: 'Music Video',
    previewImagePrompt: 'A singer performing on stage with dramatic backlighting creating silhouette rays, confetti and sparks frozen in air, wide angle lens distortion, bold color gel lighting in magenta and cyan, concert photography energy, music video production still, square format',
    previewImageId: 'e61b0293-975e-4dcf-b4b0-751639749e12',
    promptTemplate: 'Create a music video style with dynamic editing, creative visuals, and rhythmic pacing. Stylized effects, artistic transitions, energetic atmosphere.',
    previewVideoId: '20c2acc2-c7d1-4a4a-9005-0608bf033a1f',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/e61b0293-975e-4dcf-b4b0-751639749e12-sm.png',
    previewVideoCdn: 'https://cdn.gab.ai/assets/preview/videos/20c2acc2-c7d1-4a4a-9005-0608bf033a1f.mp4',
    previewGifCdn: 'https://cdn.gab.ai/assets/preview/gifs/20c2acc2-c7d1-4a4a-9005-0608bf033a1f.gif',
  },
  {
    id: 'vfx',
    name: 'VFX Heavy',
    previewImagePrompt: 'A spectacular VFX shot of a city being reconstructed by swirling golden particles and light, buildings assembling from glowing fragments, volumetric light rays, Marvel-style visual effects, photorealistic CGI integration, blockbuster movie production still, square format',
    previewImageId: '02f6daa3-0975-408d-a335-824913b4d36e',
    promptTemplate: 'Create a video with impressive visual effects, CGI elements, and spectacular visuals. Blockbuster quality, seamless integration of effects, jaw-dropping moments.',
    previewVideoId: '1eb323a8-8df3-431d-ab03-aceeb176ea22',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/02f6daa3-0975-408d-a335-824913b4d36e-sm.png',
    previewVideoCdn: 'https://cdn.gab.ai/assets/preview/videos/1eb323a8-8df3-431d-ab03-aceeb176ea22.mp4',
    previewGifCdn: 'https://cdn.gab.ai/assets/preview/gifs/1eb323a8-8df3-431d-ab03-aceeb176ea22.gif',
  },
  {
    id: 'vintage-video',
    name: 'Vintage Film',
    previewImagePrompt: 'A frame from a Super 8 home movie showing a family picnic scene, heavy film grain and scratches, warm sepia color shift, rounded corners vignette, slight motion blur and light leaks, authentic 1960s footage aesthetic, nostalgic and timeless, square format',
    previewImageId: '61ad40c4-ae25-4f7f-b0e8-1be38fce30d4',
    promptTemplate: 'Create a vintage film style video with film grain, warm tones, and nostalgic aesthetic. Retro look, classic feel, analog imperfections, timeless charm.',
    previewVideoId: '53442cac-ca3b-4416-8f4d-0c0519eb9861',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/61ad40c4-ae25-4f7f-b0e8-1be38fce30d4-sm.png',
    previewVideoCdn: 'https://cdn.gab.ai/assets/preview/videos/53442cac-ca3b-4416-8f4d-0c0519eb9861.mp4',
    previewGifCdn: 'https://cdn.gab.ai/assets/preview/gifs/53442cac-ca3b-4416-8f4d-0c0519eb9861.gif',
  },
];

// Creative prompt suggestions for the Videos page discover section
export const VIDEO_PROMPT_SUGGESTIONS = [
  {
    id: 'ocean-waves',
    text: 'Calm ocean waves gently rolling onto a sandy beach at sunset',
    thumbnail: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=200&h=200&fit=crop',
    previewImagePrompt: 'A serene ocean wave gently curling onto golden sand at sunset, warm amber and pink sky reflected in wet sand, foam patterns in the shallows, peaceful tropical beach, long exposure giving silky water texture, golden hour warmth, square format',
    previewImageId: 'a58e55e9-79d7-49c0-ab78-92d9c0bec5c7',
    previewVideoId: 'c6747094-4c29-42c2-869e-dd0af15a0eee',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/a58e55e9-79d7-49c0-ab78-92d9c0bec5c7-sm.png',
    previewVideoCdn: 'https://cdn.gab.ai/assets/preview/videos/c6747094-4c29-42c2-869e-dd0af15a0eee.mp4',
    previewGifCdn: 'https://cdn.gab.ai/assets/preview/gifs/c6747094-4c29-42c2-869e-dd0af15a0eee.gif',
  },
  {
    id: 'city-timelapse',
    text: 'Bustling city timelapse from day to night with flowing traffic lights',
    thumbnail: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=200&h=200&fit=crop',
    previewImagePrompt: 'A Manhattan skyline at blue hour with car light trails streaming through streets below, long exposure creating rivers of red and white lights, skyscrapers lit up warmly against deep blue twilight sky, urban energy captured in motion blur, square format',
    previewImageId: '2cb71ff4-c0d4-45fc-9077-b506356f378a',
    previewVideoId: '4892ae63-0ba8-4838-b3c8-d3898197b321',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/2cb71ff4-c0d4-45fc-9077-b506356f378a-sm.png',
    previewVideoCdn: 'https://cdn.gab.ai/assets/preview/videos/4892ae63-0ba8-4838-b3c8-d3898197b321.mp4',
    previewGifCdn: 'https://cdn.gab.ai/assets/preview/gifs/4892ae63-0ba8-4838-b3c8-d3898197b321.gif',
  },
  {
    id: 'forest-walk',
    text: 'Walking through an enchanted forest with sunbeams filtering through trees',
    thumbnail: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=200&h=200&fit=crop',
    previewImagePrompt: 'A misty forest path with golden sunbeams streaming through ancient redwood canopy, dust particles floating in light rays, ferns and moss covering the forest floor, first-person perspective walking forward, magical morning atmosphere, square format',
    previewImageId: '9cca9ea6-491f-444c-84ee-fc3ff0005e8e',
    previewVideoId: 'e4106ebd-28c4-4e76-abeb-3ca94df4b750',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/9cca9ea6-491f-444c-84ee-fc3ff0005e8e-sm.png',
    previewVideoCdn: 'https://cdn.gab.ai/assets/preview/videos/e4106ebd-28c4-4e76-abeb-3ca94df4b750.mp4',
    previewGifCdn: 'https://cdn.gab.ai/assets/preview/gifs/e4106ebd-28c4-4e76-abeb-3ca94df4b750.gif',
  },
  {
    id: 'space-journey',
    text: 'A journey through space passing colorful nebulas and distant galaxies',
    thumbnail: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=200&h=200&fit=crop',
    previewImagePrompt: 'Flying through a vibrant cosmic nebula in deep space, swirling clouds of magenta and teal gas, millions of stars stretching to infinity, distant spiral galaxy visible, Hubble telescope imagery style, awe-inspiring vastness of the universe, square format',
    previewImageId: 'a76792cb-9df5-42b0-8941-009230f05d00',
    previewVideoId: 'b4e36315-a94a-4ecd-9508-0ac6a0ec3847',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/a76792cb-9df5-42b0-8941-009230f05d00-sm.png',
    previewVideoCdn: 'https://cdn.gab.ai/assets/preview/videos/b4e36315-a94a-4ecd-9508-0ac6a0ec3847.mp4',
    previewGifCdn: 'https://cdn.gab.ai/assets/preview/gifs/b4e36315-a94a-4ecd-9508-0ac6a0ec3847.gif',
  },
  {
    id: 'coffee-pour',
    text: 'Slow motion coffee being poured into a cup with cream swirling',
    thumbnail: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=200&h=200&fit=crop',
    previewImagePrompt: 'A close-up of cream being poured into dark coffee, beautiful swirling marble patterns forming, warm morning light, ceramic cup on wooden table, frozen moment of mixing liquids, food photography aesthetic, cozy cafe atmosphere, square format',
    previewImageId: '0d124407-280d-4be2-96e5-c54b592ac54c',
    previewVideoId: '9fbfeefd-a8b8-4423-bf9e-d9f6e55fd4a6',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/0d124407-280d-4be2-96e5-c54b592ac54c-sm.png',
    previewVideoCdn: 'https://cdn.gab.ai/assets/preview/videos/9fbfeefd-a8b8-4423-bf9e-d9f6e55fd4a6.mp4',
    previewGifCdn: 'https://cdn.gab.ai/assets/preview/gifs/9fbfeefd-a8b8-4423-bf9e-d9f6e55fd4a6.gif',
  },
  {
    id: 'northern-lights',
    text: 'Aurora borealis dancing across the night sky over mountains',
    thumbnail: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=200&h=200&fit=crop',
    previewImagePrompt: 'Spectacular aurora borealis in electric green and violet sweeping across Arctic night sky, snow-covered mountain range silhouette, reflection on frozen fjord below, countless stars visible, Scandinavian wilderness, breathtaking natural light show, square format',
    previewImageId: '928b9b24-7fb8-45fc-9722-9c31792e8afa',
    previewVideoId: 'ca9465f2-b69d-4e5a-8c20-5ba149489e39',
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/928b9b24-7fb8-45fc-9722-9c31792e8afa-sm.png',
    previewVideoCdn: 'https://cdn.gab.ai/assets/preview/videos/ca9465f2-b69d-4e5a-8c20-5ba149489e39.mp4',
    previewGifCdn: 'https://cdn.gab.ai/assets/preview/gifs/ca9465f2-b69d-4e5a-8c20-5ba149489e39.gif',
  },
];

export const THREE_D_PROMPT_SUGGESTIONS = [
  {
    id: 'fantasy-sword',
    text: 'An ornate fantasy sword with a glowing crystal in the hilt',
  },
  {
    id: 'robot-companion',
    text: 'A cute rounded robot companion with expressive eyes',
  },
  {
    id: 'space-helmet',
    text: 'A futuristic astronaut helmet with glass visor and detailed panels',
  },
  {
    id: 'treasure-chest',
    text: 'An old pirate treasure chest with metal bands and carved wood',
  },
  {
    id: 'dragon-statue',
    text: 'A small dragon statue perched on a stone base',
  },
  {
    id: 'sci-fi-crate',
    text: 'A game-ready sci-fi supply crate with worn metal edges',
  },
];

// Music generation styles for the Music page
export const MUSIC_STYLES = [
  {
    id: 'electronic',
    name: 'Electronic',
    previewImagePrompt: 'Close-up of hands on a vintage analog synthesizer with glowing knobs and sliders, dark club environment with purple and cyan laser beams cutting through haze, LED VU meters pulsing, abstract waveform visualizations floating in the background, square format',
    previewImageId: 'da34a858-5210-49e4-9340-5c15b96a77c5',
    promptTemplate: 'Create an electronic music track with synthesizers, driving beats, and atmospheric pads. Modern production, energetic rhythm, club-ready sound.',
    exampleLyrics: [
      "Lost in the neon lights tonight\nFeeling the bass drop, everything's right\nWe're dancing on the edge of time\nIn this electric world, you're mine",
      "Synthesizers calling out my name\nEvery beat ignites this digital flame\nWe're connected through the sound\nIn this frequency, we're found",
      "Pulse of the city in my veins\nLaser lights breaking through the rain\nTonight we lose ourselves to sound\nOn this dancefloor, we're unbound",
    ],
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/da34a858-5210-49e4-9340-5c15b96a77c5-sm.png',
  },
  {
    id: 'orchestral',
    name: 'Orchestral',
    previewImagePrompt: 'A grand symphony orchestra performing in an ornate concert hall, conductor with baton raised dramatically, string section in foreground with warm stage lighting, golden ceiling details, musicians in formal attire, powerful and majestic atmosphere, square format',
    previewImageId: '41c1f24f-037a-4258-8128-c3f8953948d4',
    promptTemplate: 'Create an orchestral composition with sweeping strings, powerful brass, and dynamic percussion. Cinematic feel, emotional depth, epic arrangement.',
    exampleLyrics: [
      "Rise above the mountains high\nWhere eagles soar across the sky\nA symphony of courage calls\nThrough ancient stone and castle walls",
      "In twilight's gentle fading glow\nWhere rivers of emotion flow\nThe strings cry out a tale untold\nOf heroes brave and hearts of gold",
      "March forward into destiny\nWith drums of war and melody\nThe brass announces glory's dawn\nA legacy that carries on",
    ],
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/41c1f24f-037a-4258-8128-c3f8953948d4-sm.png',
  },
  {
    id: 'jazz',
    name: 'Jazz',
    previewImagePrompt: 'A saxophone player in a smoky jazz club, dramatic side lighting creating rim light on brass instrument, intimate small stage with upright bass player in shadows behind, exposed brick walls, vintage microphone, late-night New Orleans speakeasy atmosphere, square format',
    previewImageId: '2186d20a-3efc-4082-ae1d-4ec329aafa97',
    promptTemplate: 'Create a jazz piece with smooth saxophone, walking bass, and brushed drums. Sophisticated harmonies, improvised feel, late-night ambiance.',
    exampleLyrics: [
      "Smoky room, the lights are low\nSaxophone plays soft and slow\nYour eyes meet mine across the bar\nYou're my midnight shooting star",
      "Walking bass beneath my feet\nEvery note is bittersweet\nIn this jazz club we call home\nWe never have to be alone",
      "Brush strokes on the snare so light\nWe'll improvise into the night\nThe melody flows like good wine\nIn three-quarter time, you're mine",
    ],
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/2186d20a-3efc-4082-ae1d-4ec329aafa97-sm.png',
  },
  {
    id: 'lo-fi',
    name: 'Lo-Fi',
    previewImagePrompt: 'A cozy bedroom desk at night with vintage record player spinning vinyl, warm lamp glow, rain on window in background, steaming cup of tea, open notebook and pen, headphones resting nearby, anime lo-fi study girl aesthetic, nostalgic and peaceful, square format',
    previewImageId: '9aaebdca-08dc-42d1-b4fc-ff795958cee2',
    promptTemplate: 'Create a lo-fi hip hop beat with dusty samples, relaxed drums, and warm vinyl crackle. Chill vibes, study music, nostalgic atmosphere.',
    exampleLyrics: [
      "Rain on the window, coffee's warm\nVinyl crackles, weathering the storm\nPages turning, thoughts drift by\nIn this moment, time goes slow",
      "Late night thoughts and city lights\nHeadphones on, everything's right\nDusty samples fill the air\nFinding peace just sitting here",
      "Memories like photographs\nFaded colors, gentle laughs\nLet the mellow beats roll on\nUntil the early morning dawn",
    ],
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/9aaebdca-08dc-42d1-b4fc-ff795958cee2-sm.png',
  },
  {
    id: 'rock',
    name: 'Rock',
    previewImagePrompt: 'An electric guitar mid-air during a rock concert, dramatic stage pyrotechnics and sparks in background, motion blur from energetic performance, Les Paul or Stratocaster shape, crowd silhouettes below, raw power and rebellion energy, arena rock atmosphere, square format',
    previewImageId: 'f758d1fb-99f2-4e09-85ff-6fa5368bd856',
    promptTemplate: 'Create a rock track with powerful guitar riffs, driving drums, and energetic bass. Raw energy, anthemic feel, stadium-ready sound.',
    exampleLyrics: [
      "Turn it up, feel the power surge\nGuitar screaming on the verge\nWe came to rock, we came to fight\nThis is our anthem tonight!",
      "Breaking chains, we're breaking free\nThis is who we're meant to be\nFists in the air, voices loud\nWe are the unstoppable crowd!",
      "Stadium lights are burning bright\nWe're taking over tonight\nEvery chord, every beat\nMakes this moment feel complete!",
    ],
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/f758d1fb-99f2-4e09-85ff-6fa5368bd856-sm.png',
  },
  {
    id: 'ambient',
    name: 'Ambient',
    previewImagePrompt: 'An abstract ethereal cloudscape at dawn, soft gradients of lavender pink and pale gold, floating translucent layers like mist, meditative and serene atmosphere, minimalist composition with vast empty space, Brian Eno album cover aesthetic, dreamy and transcendent, square format',
    previewImageId: 'd0b81b68-afb5-4591-bb7b-084832840643',
    promptTemplate: 'Create an ambient soundscape with ethereal textures, gentle drones, and spatial effects. Meditative, atmospheric, perfect for relaxation.',
    exampleLyrics: [
      "Breathe in slowly, let it go\nWatch the clouds drift soft and slow\nIn this space between the stars\nHealing all our hidden scars",
      "Floating through an endless dream\nNothing's quite the way it seems\nGentle waves wash over me\nFinding peace eternally",
      "Silence speaks in whispered tones\nIn the quiet, you're not alone\nLet the ambient sounds embrace\nThis serene and sacred space",
    ],
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/d0b81b68-afb5-4591-bb7b-084832840643-sm.png',
  },
  {
    id: 'hip-hop',
    name: 'Hip Hop',
    previewImagePrompt: 'A vintage Akai MPC drum machine with hands programming beats, gold chains visible at wrists, moody red and purple studio lighting, vinyl records stacked nearby, classic boom bap meets modern trap aesthetic, urban producer environment, square format',
    previewImageId: '8e78ea2a-9300-4b50-b9ca-f2026783372b',
    promptTemplate: 'Create a hip hop beat with hard-hitting drums, deep 808 bass, and catchy melodies. Modern trap influence, head-nodding groove, street-ready.',
    exampleLyrics: [
      "Started from the bottom now we here\nEvery step I took brought me near\nTo the top where the view is clear\nNo more doubt, no more fear",
      "808s hitting in my chest\nPutting all my dreams to the test\nGrinding hard, no time for rest\nOn a mission to be the best",
      "Real recognize real, that's facts\nStaying true while I make these tracks\nFrom the block to the world map\nNothing's gonna hold me back",
    ],
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/8e78ea2a-9300-4b50-b9ca-f2026783372b-sm.png',
  },
  {
    id: 'classical',
    name: 'Classical',
    previewImagePrompt: 'Close-up of elegant hands playing a grand piano, candlelight reflecting off polished black lacquer, sheet music visible with handwritten notes, romantic period salon setting, soft focus background with velvet curtains, Chopin or Liszt recital atmosphere, intimate and refined, square format',
    previewImageId: '508651f9-0a3f-4550-b1b7-c7f982e364dc',
    promptTemplate: 'Create a classical piano piece with elegant melodies, expressive dynamics, and refined harmonies. Romantic era style, emotional and beautiful.',
    exampleLyrics: [
      "In candlelight the piano plays\nA melody from distant days\nEach note a tear, each chord a sigh\nOf love that never said goodbye",
      "Through moonlit halls the music rings\nOf all the joy that sorrow brings\nThe keys tell tales of hearts that yearn\nFor those who left and won't return",
      "Ivory dreams in minor key\nOf what we were and what could be\nThe final note hangs in the air\nA whispered hope, a silent prayer",
    ],
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/508651f9-0a3f-4550-b1b7-c7f982e364dc-sm.png',
  },
];

// Creative prompt suggestions for the Music page discover section
export const MUSIC_PROMPT_SUGGESTIONS = [
  {
    id: 'epic-trailer',
    name: 'Epic Trailer',
    text: 'Epic cinematic trailer music with building tension and triumphant climax',
    thumbnail: 'https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=200&h=200&fit=crop',
    previewImagePrompt: 'A dramatic movie poster style image of a lone warrior standing atop a cliff overlooking a vast army, storm clouds gathering, lightning in distance, epic scale composition, orange and teal color grading, Hans Zimmer soundtrack visualization, blockbuster trailer energy, square format',
    previewImageId: '4fd9f663-2e04-4a10-9658-c9ccb1230070',
    promptTemplate: 'Epic cinematic trailer music with building tension and triumphant climax. Orchestral arrangement with powerful percussion and soaring melodies.',
    exampleLyrics: [
      "From the ashes we will rise\nReaching for the endless skies\nEvery battle, every fight\nLeads us to the morning light",
      "Heroes standing side by side\nNothing left to fear or hide\nThe moment's here, the time is now\nWe will find a way somehow",
      "Against all odds we carry on\nUntil the final war is won\nOur legacy will never fade\nIn history, our mark is made",
    ],
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/4fd9f663-2e04-4a10-9658-c9ccb1230070-sm.png',
  },
  {
    id: 'chill-beats',
    name: 'Chill Beats',
    text: 'Relaxing lo-fi beats perfect for studying or working late at night',
    thumbnail: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=200&h=200&fit=crop',
    previewImagePrompt: 'A cozy late night study scene with laptop open on desk, soft warm lamp light, rain streaking down window reflecting city lights below, coffee mug and scattered books, lo-fi girl anime aesthetic, peaceful productive midnight atmosphere, square format',
    previewImageId: '53636ff9-cd9a-4372-9a9e-86177087db15',
    promptTemplate: 'Relaxing lo-fi hip hop beats perfect for studying or working late at night. Mellow drums, warm samples, and cozy atmosphere.',
    exampleLyrics: [
      "Late night thoughts drift away\nAnother cup, another day\nPages turn, the candle burns\nIn quiet moments, the mind learns",
      "Raindrops on my window pane\nWashing away the stress and strain\nJust me, my thoughts, and this beat\nFinding peace in the retreat",
      "Coffee steam rises slow\nWatching the city lights below\nDeadlines fade into the haze\nLost in these lo-fi days",
    ],
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/53636ff9-cd9a-4372-9a9e-86177087db15-sm.png',
  },
  {
    id: 'nature-sounds',
    name: 'Nature Sounds',
    text: 'Peaceful nature soundscape with gentle rain and distant thunder',
    thumbnail: 'https://images.unsplash.com/photo-1501691223387-dd0500403074?w=200&h=200&fit=crop',
    previewImagePrompt: 'A peaceful forest scene during gentle rainfall, water droplets on leaves in sharp focus, misty atmosphere between trees, soft diffused light, a small stream visible in background, lush green moss on rocks, ASMR nature photography aesthetic, calming and refreshing, square format',
    previewImageId: 'e6e00424-7b66-4767-b355-ad356291d5e6',
    promptTemplate: 'Peaceful nature soundscape with gentle rain, distant thunder, and forest ambiance. Perfect for relaxation and sleep.',
    exampleLyrics: [
      "Listen to the rain fall down\nWashing over this old town\nThunder rolling far away\nNature singing through the day",
      "Leaves are dancing in the breeze\nSunlight filtering through the trees\nA river whispers as it flows\nWhere it leads, nobody knows",
      "Morning dew on petals bright\nBirds awakening with first light\nThe world breathes in, the world breathes out\nThis is what peace is about",
    ],
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/e6e00424-7b66-4767-b355-ad356291d5e6-sm.png',
  },
  {
    id: 'retro-synth',
    name: 'Retro Synthwave',
    text: '80s retro synthwave with pulsing arpeggios and neon-soaked atmosphere',
    thumbnail: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=200&h=200&fit=crop',
    previewImagePrompt: 'A retro 1980s DeLorean driving on a neon grid highway toward a glowing sunset horizon, chrome and hot pink reflections, palm tree silhouettes, scanline CRT monitor effect, Outrun video game aesthetic, vaporwave meets synthwave nostalgia, square format',
    previewImageId: 'e87932df-c6f7-4866-8ccc-a7cf98ed6bad',
    promptTemplate: '80s retro synthwave with pulsing arpeggios, driving bass, and neon-soaked atmosphere. Nostalgic and energetic.',
    exampleLyrics: [
      "Neon lights reflect on chrome\nDriving fast so far from home\nThe city sleeps but we're alive\nIn the night we thrive",
      "Synthesizers fill the air\nElectric dreams are everywhere\nRewind to 1985\nWhen the future came alive",
      "Arcade glow and laser beams\nLiving out our wildest dreams\nThe night is young, the beat is strong\nThis is where we belong",
    ],
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/e87932df-c6f7-4866-8ccc-a7cf98ed6bad-sm.png',
  },
  {
    id: 'acoustic-folk',
    name: 'Acoustic Folk',
    text: 'Warm acoustic folk song with fingerpicked guitar and gentle vocals',
    thumbnail: 'https://images.unsplash.com/photo-1510915361894-db8b60106cb1?w=200&h=200&fit=crop',
    previewImagePrompt: 'A worn acoustic guitar resting against a wooden cabin porch railing at golden hour, mountains visible in soft focus background, wildflowers in foreground, weathered wood textures, Americana folk aesthetic, warm nostalgic feeling of country living, square format',
    previewImageId: '7d639975-1938-4202-b73c-a31926f4629c',
    promptTemplate: 'Warm acoustic folk song with fingerpicked guitar, gentle vocals, and heartfelt storytelling. Intimate and authentic.',
    exampleLyrics: [
      "Sitting on the porch at dusk\nGuitar strings covered in dust\nSinging songs my father taught\nMemories that can't be bought",
      "Down the winding country road\nCarrying this heavy load\nBut the music sets me free\nThis is who I'm meant to be",
      "Campfire crackling in the night\nStars above are shining bright\nFriends gathered all around\nIn this moment, peace is found",
    ],
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/7d639975-1938-4202-b73c-a31926f4629c-sm.png',
  },
  {
    id: 'meditation',
    name: 'Meditation',
    text: 'Calming meditation music with singing bowls and soft ambient textures',
    thumbnail: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=200&h=200&fit=crop',
    previewImagePrompt: 'A Tibetan singing bowl with mallet resting inside, surrounded by smooth river stones and a single lotus flower, soft morning light creating gentle shadows, zen garden sand patterns, minimalist meditation space, peaceful and grounding energy, square format',
    previewImageId: 'd378b271-eeb4-4efc-a8da-fb7a205c8af3',
    promptTemplate: 'Calming meditation music with singing bowls, soft ambient textures, and gentle drones. Perfect for mindfulness and relaxation.',
    exampleLyrics: [
      "Breathe in deep, let it go\nWatch your thoughts drift to and fro\nIn this moment, find your peace\nLet all your worries release",
      "Quiet mind, open heart\nEvery ending is a start\nPresent moment, here and now\nTo the stillness, gently bow",
      "Light within begins to glow\nLetting go of all you know\nIn the silence, find your way\nRest in peace throughout the day",
    ],
    previewImageCdn: 'https://cdn.gab.ai/assets/preview/images/d378b271-eeb4-4efc-a8da-fb7a205c8af3-sm.png',
  },
];

// defaults, use model settings to override
export const CONTEXT_NOTIFICATION_LIMITS = {
  INPUT_LENGTH_MAX: 50000,       // tokens - 50k
  LARGE_INPUT_WARNING: 25000,    // tokens - large prompts can materially increase time-to-first-token

  CONTEXT_LENGTH_WARNING: 80000, // tokens - 80k (legacy fallback when no model context window known)
  CONTEXT_LENGTH_MAX: 1000000, // tokens - 1M (legacy)

  // Show the proactive "this chat is getting long, older messages will be
  // summarized to keep it going" banner once the conversation has used this
  // percentage of the model's context window. Picked to match the backend's
  // ContextWindowController.THRESHOLDS.COMPACTION_RATIO so the user is warned
  // BEFORE auto-summarization actually fires (proactive, not reactive).
  CONTEXT_LENGTH_WARN_PCT: 70,

  MESSAGES_WARNING: 250, // messages - 250 (proactive UX nudge)
  MESSAGES_MAX: 5000, // messages - safety ceiling only (was 350 UX limit; now matches backend CONTEXT_LIMITS.MESSAGES_MAX)
};

export const RESPONSE_ACTION_TYPES = {
  VERIFY_EMAIL: 'resend-verification',
  GHOST_SIGNUP: 'ghost-signup',
  LOGIN: 'login-first',
  GO_PLUS: 'go-plus',
  BUY_CREDITS: 'buy-credits',
  GET_CREDITS: 'get-credits', // For free users - shows OutOfCreditsModal
  TRY_AGENTS: 'try-agents',
  RETRY: 'retry',
  MAKE_NEW_CONVERSATION: 'make-new-conversation',
  MAKE_NEW_CUSTOM_AGENT: 'make-new-custom-agent',
  VIEW_FILE: 'view-file',
  CONTENT_MODERATION: 'content-moderation',
  ADD_TO_BOOKMARK_FOLDER: 'add-to-bookmark-folder',
  UNDO_MEMORY: 'undo-memory',
};

export const DEFAULT_AI_STARTERS = [
  "starters.blog_post_internet",
  "starters.workout_plan",
  "starters.time_travel_story",
  "starters.business_proposal",
  "starters.product_description",
  "starters.meal_prep",
  "starters.marketing_strategy",
  "starters.cover_letter",
  "starters.birthday_party",
  "starters.content_calendar",
  "starters.explainer_video",
  "starters.budget_spreadsheet",
  "starters.book_outline",
  "starters.social_media_strategy",
  "starters.presentation_deck",
  "starters.interview_prep",
  "starters.garden_planning",
  "starters.fitness_goals",
  "starters.meditation_guide",
  "starters.language_practice",
  "starters.resume_update",
  "starters.event_planning",
  "starters.business_name",
  "starters.email_template",
  "starters.podcast_outline",
  "starters.diet_plan",
  "starters.home_workout",
  "starters.story_children",
  "starters.photo_tips",
  "starters.music_playlist",
  "starters.art_project",
  "starters.public_speech",
  "starters.productivity_tips",
  "starters.wedding_planning",
  "starters.career_advice",
  "starters.home_organization",
  "starters.pet_care",
  "starters.financial_goals",
  "starters.coding_project",
  "starters.mindfulness_routine",
  "starters.fashion_advice",
  "starters.zero_waste",
  "starters.book_club",
  "starters.family_activities",
  "starters.self_improvement",
  "starters.knowledge_test_prep",
  "starters.python_script_automation",
  "starters.wedding_plus_one",
  "starters.morning_routing",
  "starters.plan_nashville_trip",
  "starters.improve_english_grammar",
  "starters.analyze_research_paper",
  "starters.personal_brand_strategy",
  "starters.learn_programming_path",
  "starters.sustainability_home_tips",
  "starters.book_recommendation",
  "starters.travel_photography_guide",
  "starters.healthy_meal_substitutes",
  "starters.interview_questions",
  "starters.data_visualization",
  "starters.creative_writing_prompt",
  "starters.home_office_setup",
  "starters.startup_pitch",
  "starters.business_plan_financial",
  "starters.customer_survey",
  "starters.sales_strategy",
  "starters.investor_presentation",
  "starters.academic_research",
  "starters.study_schedule",
  "starters.literature_review",
  "starters.grant_proposal",
  "starters.team_building",
  "starters.hiring_strategy",
  "starters.process_optimization",
  "starters.risk_assessment",
  "starters.expansion_strategy",
  "starters.board_presentation",
];

export const LOCAL_TOOLTIP_NOTIS = {
  TOOLS_NEW_10022025: {
    key: 'TOOLTIP_NOTIS_TOOLS_NEW_10022025',
    title: 'NEW',
    expiresAt: '11-02-2025',
  },
  COLLECTION_FILES_NEW_10022025: {
    key: 'TOOLTIP_NOTIS_COLLECTION_FILES_NEW_10022025',
    title: 'NEW',
    expiresAt: '11-02-2025',
  },
};

export const PROFESSIONAL_ROLES = [
  { value: 'student', label: 'Student', icon: 'graduation-cap', featured: true },
  { value: 'engineering', label: 'Engineering', icon: 'microchip', featured: true },
  { value: 'small_business', label: 'Small Business Owner', icon: 'store', featured: true },
  { value: 'homemaker', label: 'Homemaker', icon: 'house', featured: true },
  { value: 'ministry', label: 'Pastor / Ministry', icon: 'church', featured: true },
  { value: 'education', label: 'Teacher / Educator', icon: 'chalkboard-user', featured: true },
  { value: 'creator', label: 'Creator / Writer', icon: 'pen-nib', featured: true },
  { value: 'healthcare', label: 'Healthcare', icon: 'stethoscope', featured: true },
  { value: 'product_management', label: 'Product Management', icon: 'diagram-project' },
  { value: 'human_resources', label: 'Human Resources', icon: 'users' },
  { value: 'finance', label: 'Finance', icon: 'chart-line' },
  { value: 'marketing', label: 'Marketing', icon: 'bullhorn' },
  { value: 'sales', label: 'Sales', icon: 'handshake' },
  { value: 'operations', label: 'Operations', icon: 'gears' },
  { value: 'data_science', label: 'Data Science', icon: 'flask' },
  { value: 'design', label: 'Design', icon: 'palette' },
  { value: 'legal', label: 'Legal', icon: 'scale-balanced' },
  { value: 'trades', label: 'Trades / Construction', icon: 'hammer' },
  { value: 'agriculture', label: 'Farming / Agriculture', icon: 'tractor' },
  { value: 'real_estate', label: 'Real Estate', icon: 'building' },
  { value: 'retired', label: 'Retired', icon: 'people-roof' },
  { value: 'other', label: 'Other', icon: 'briefcase', featured: true },
];

export const TURNSTILE_SITE_KEY = process.env.EXPO_PUBLIC_TURNSTILE_SITE_KEY || '0x4AAAAAAAQeXKSGTzxYAKT8';

// Ad platform pixel IDs removed 2026-09 when the Facebook and Reddit pixels
// were retired. Backend env vars FB_PIXEL_ID / REDDIT_PIXEL_ID are also
// disabled — see backend/utils/facebook_capi.js and reddit_capi.js.

// SSO client IDs (public — embedded in frontend bundle)
export const GOOGLE_OAUTH_CLIENT_ID_WEB = '822974377574-t0tqqeg9vfks787f4c5in1qfq6l5fgdo.apps.googleusercontent.com';
export const GOOGLE_OAUTH_CLIENT_ID_IOS = '822974377574-34meeoodqhb42i0e3iptnp8lqmr5othc.apps.googleusercontent.com';
export const APPLE_SSO_CLIENT_ID = 'ai.gab.web.signin';
